import React from "react";
import { Formik } from "formik";

import {
  hasMeaningfulValue,
  isValidAssetReference,
  isValidEmail,
  isValidPhoneNumber,
} from "./validation";
import {
  ActionRow,
  FieldGroup,
  FieldLabel,
  FormGrid,
  FormStack,
  PrimaryButton,
  SecondaryButton,
  SelectInput,
  TextInput,
  ValidationErrorText,
  WideField,
} from "./styles/admin-forms.styles";
import { ImagePickerField } from "./shared-form-fields";
import { UserFormValues } from "./types";

const allowedRoles = new Set(["user", "player", "admin"]);

const trimOptionalValue = (value?: string) => {
  const trimmed = value?.trim() || "";
  return trimmed || undefined;
};

const normalizeAdminUserValues = (values: UserFormValues): UserFormValues => ({
  ...values,
  city: trimOptionalValue(values.city),
  country: trimOptionalValue(values.country),
  email: values.email.trim(),
  first_name: values.first_name.trim(),
  last_name: values.last_name.trim(),
  phone_number: trimOptionalValue(values.phone_number),
  role: trimOptionalValue(values.role) || "user",
  state: trimOptionalValue(values.state),
  street_address: trimOptionalValue(values.street_address),
  zip_code: trimOptionalValue(values.zip_code),
});

const validateAdminUserValues = (
  values: UserFormValues,
  {
    includeRole,
    requirePassword,
  }: {
    includeRole: boolean;
    requirePassword: boolean;
  },
) => {
  const errors: Partial<Record<keyof UserFormValues, string>> = {};
  const normalizedPassword = values.password || "";
  const normalizedPasswordConfirmation = values.password_confirmation || "";

  if (!values.first_name.trim()) {
    errors.first_name = "First name is required.";
  }

  if (!values.last_name.trim()) {
    errors.last_name = "Last name is required.";
  }

  if (!values.email.trim()) {
    errors.email = "Email is required.";
  } else if (!isValidEmail(values.email)) {
    errors.email = "Enter a valid email address.";
  }

  if (values.phone_number?.trim() && !isValidPhoneNumber(values.phone_number)) {
    errors.phone_number = "Enter a valid phone number.";
  }

  if (includeRole && values.role && !allowedRoles.has(values.role)) {
    errors.role = "Choose a valid role.";
  }

  if (requirePassword && !normalizedPassword.trim()) {
    errors.password = "Password is required.";
  }

  if (requirePassword && !normalizedPasswordConfirmation.trim()) {
    errors.password_confirmation = "Password confirmation is required.";
  }

  if (normalizedPassword && normalizedPassword.length < 8) {
    errors.password = "Password must be at least 8 characters.";
  }

  if (
    hasMeaningfulValue(normalizedPassword) ||
    hasMeaningfulValue(normalizedPasswordConfirmation)
  ) {
    if (!normalizedPassword.trim()) {
      errors.password = "Password is required when confirmation is provided.";
    }

    if (!normalizedPasswordConfirmation.trim()) {
      errors.password_confirmation =
        "Password confirmation is required when a password is provided.";
    }

    if (
      normalizedPassword.trim() &&
      normalizedPasswordConfirmation.trim() &&
      normalizedPassword !== normalizedPasswordConfirmation
    ) {
      errors.password_confirmation = "Password confirmation must match.";
    }
  }

  if (
    typeof values.image === "string" &&
    values.image.trim() &&
    !isValidAssetReference(values.image)
  ) {
    errors.image = "Profile image must be an upload or a valid asset URL.";
  }

  return errors;
};

export const AdminUserForm: React.FC<{
  initialValues: UserFormValues;
  isSubmitting?: boolean;
  onCancel: () => void;
  onSubmit: (values: UserFormValues) => Promise<void> | void;
  includeRole?: boolean;
  requirePassword?: boolean;
  submitLabel?: string;
}> = ({
  includeRole = true,
  initialValues,
  isSubmitting = false,
  onCancel,
  onSubmit,
  requirePassword = false,
  submitLabel,
}) => (
  <Formik<UserFormValues>
    enableReinitialize
    initialValues={initialValues}
    validate={(values) => validateAdminUserValues(values, { includeRole, requirePassword })}
    onSubmit={async (values) => {
      await Promise.resolve(onSubmit(normalizeAdminUserValues(values)));
    }}
  >
    {({
      errors,
      handleSubmit,
      isSubmitting: formikSubmitting,
      setFieldTouched,
      setFieldValue,
      submitCount,
      touched,
      values,
    }) => {
      const fieldIds = {
        city: "admin-user-city",
        country: "admin-user-country",
        email: "admin-user-email",
        firstName: "admin-user-first-name",
        lastName: "admin-user-last-name",
        password: "admin-user-password",
        passwordConfirmation: "admin-user-password-confirmation",
        phoneNumber: "admin-user-phone-number",
        role: "admin-user-role",
        state: "admin-user-state",
        streetAddress: "admin-user-street-address",
        zipCode: "admin-user-zip-code",
      } as const;

      const getError = (fieldName: keyof UserFormValues) =>
        errors[fieldName] && (touched[fieldName] || submitCount > 0)
          ? errors[fieldName]
          : undefined;

      const imageError =
        errors.image && (touched.image || submitCount > 0) ? errors.image : undefined;

      return (
        <form onSubmit={handleSubmit}>
          <FormStack>
            <FormGrid>
              <FieldGroup>
                <FieldLabel htmlFor={fieldIds.firstName}>First name</FieldLabel>
                <TextInput
                  aria-invalid={Boolean(getError("first_name"))}
                  id={fieldIds.firstName}
                  name="first_name"
                  onBlur={() => setFieldTouched("first_name", true, false)}
                  onChange={(event) => setFieldValue("first_name", event.target.value)}
                  required
                  value={values.first_name || ""}
                />
                {getError("first_name") ? (
                  <ValidationErrorText>{getError("first_name")}</ValidationErrorText>
                ) : null}
              </FieldGroup>

              <FieldGroup>
                <FieldLabel htmlFor={fieldIds.lastName}>Last name</FieldLabel>
                <TextInput
                  aria-invalid={Boolean(getError("last_name"))}
                  id={fieldIds.lastName}
                  name="last_name"
                  onBlur={() => setFieldTouched("last_name", true, false)}
                  onChange={(event) => setFieldValue("last_name", event.target.value)}
                  required
                  value={values.last_name || ""}
                />
                {getError("last_name") ? (
                  <ValidationErrorText>{getError("last_name")}</ValidationErrorText>
                ) : null}
              </FieldGroup>

              <WideField>
                <FieldGroup>
                  <FieldLabel htmlFor={fieldIds.email}>Email</FieldLabel>
                  <TextInput
                    aria-invalid={Boolean(getError("email"))}
                    id={fieldIds.email}
                    name="email"
                    onBlur={() => setFieldTouched("email", true, false)}
                    onChange={(event) => setFieldValue("email", event.target.value)}
                    required
                    type="email"
                    value={values.email || ""}
                  />
                  {getError("email") ? (
                    <ValidationErrorText>{getError("email")}</ValidationErrorText>
                  ) : null}
                </FieldGroup>
              </WideField>

              <FieldGroup>
                <FieldLabel htmlFor={fieldIds.phoneNumber}>Phone number</FieldLabel>
                <TextInput
                  aria-invalid={Boolean(getError("phone_number"))}
                  id={fieldIds.phoneNumber}
                  name="phone_number"
                  onBlur={() => setFieldTouched("phone_number", true, false)}
                  onChange={(event) => setFieldValue("phone_number", event.target.value)}
                  value={values.phone_number || ""}
                />
                {getError("phone_number") ? (
                  <ValidationErrorText>{getError("phone_number")}</ValidationErrorText>
                ) : null}
              </FieldGroup>

              {includeRole ? (
                <FieldGroup>
                  <FieldLabel htmlFor={fieldIds.role}>Role</FieldLabel>
                  <SelectInput
                    aria-invalid={Boolean(getError("role"))}
                    id={fieldIds.role}
                    name="role"
                    onBlur={() => setFieldTouched("role", true, false)}
                    onChange={(event) => setFieldValue("role", event.target.value)}
                    value={values.role || "user"}
                  >
                    <option value="user">User</option>
                    <option value="player">Player</option>
                    <option value="admin">Admin</option>
                  </SelectInput>
                  {getError("role") ? (
                    <ValidationErrorText>{getError("role")}</ValidationErrorText>
                  ) : null}
                </FieldGroup>
              ) : null}

              <WideField>
                <FieldGroup>
                  <FieldLabel htmlFor={fieldIds.streetAddress}>Street address</FieldLabel>
                  <TextInput
                    id={fieldIds.streetAddress}
                    name="street_address"
                    onBlur={() => setFieldTouched("street_address", true, false)}
                    onChange={(event) => setFieldValue("street_address", event.target.value)}
                    value={values.street_address || ""}
                  />
                </FieldGroup>
              </WideField>

              <FieldGroup>
                <FieldLabel htmlFor={fieldIds.city}>City</FieldLabel>
                <TextInput
                  id={fieldIds.city}
                  name="city"
                  onBlur={() => setFieldTouched("city", true, false)}
                  onChange={(event) => setFieldValue("city", event.target.value)}
                  value={values.city || ""}
                />
              </FieldGroup>

              <FieldGroup>
                <FieldLabel htmlFor={fieldIds.state}>State</FieldLabel>
                <TextInput
                  id={fieldIds.state}
                  name="state"
                  onBlur={() => setFieldTouched("state", true, false)}
                  onChange={(event) => setFieldValue("state", event.target.value)}
                  value={values.state || ""}
                />
              </FieldGroup>

              <FieldGroup>
                <FieldLabel htmlFor={fieldIds.country}>Country</FieldLabel>
                <TextInput
                  id={fieldIds.country}
                  name="country"
                  onBlur={() => setFieldTouched("country", true, false)}
                  onChange={(event) => setFieldValue("country", event.target.value)}
                  value={values.country || ""}
                />
              </FieldGroup>

              <FieldGroup>
                <FieldLabel htmlFor={fieldIds.zipCode}>Zip code</FieldLabel>
                <TextInput
                  id={fieldIds.zipCode}
                  name="zip_code"
                  onBlur={() => setFieldTouched("zip_code", true, false)}
                  onChange={(event) => setFieldValue("zip_code", event.target.value)}
                  value={values.zip_code || ""}
                />
              </FieldGroup>

              <FieldGroup>
                <FieldLabel htmlFor={fieldIds.password}>Password</FieldLabel>
                <TextInput
                  aria-invalid={Boolean(getError("password"))}
                  id={fieldIds.password}
                  name="password"
                  onBlur={() => setFieldTouched("password", true, false)}
                  onChange={(event) => setFieldValue("password", event.target.value)}
                  required={requirePassword}
                  type="password"
                  value={values.password || ""}
                />
                {getError("password") ? (
                  <ValidationErrorText>{getError("password")}</ValidationErrorText>
                ) : null}
              </FieldGroup>

              <FieldGroup>
                <FieldLabel htmlFor={fieldIds.passwordConfirmation}>Confirm password</FieldLabel>
                <TextInput
                  aria-invalid={Boolean(getError("password_confirmation"))}
                  id={fieldIds.passwordConfirmation}
                  name="password_confirmation"
                  onBlur={() => setFieldTouched("password_confirmation", true, false)}
                  onChange={(event) => setFieldValue("password_confirmation", event.target.value)}
                  required={requirePassword}
                  type="password"
                  value={values.password_confirmation || ""}
                />
                {getError("password_confirmation") ? (
                  <ValidationErrorText>{getError("password_confirmation")}</ValidationErrorText>
                ) : null}
              </FieldGroup>
            </FormGrid>

            <div onBlur={() => setFieldTouched("image", true, false)}>
              <ImagePickerField
                helper="Upload a profile image directly or keep using an external hosted URL."
                label="Profile image"
                onChange={(value) => {
                  setFieldTouched("image", true, false);
                  setFieldValue("image", value);
                }}
                placeholder="https://example.com/profile.jpg"
                value={values.image}
              />
              {imageError ? <ValidationErrorText>{imageError}</ValidationErrorText> : null}
            </div>

            <ActionRow>
              <SecondaryButton onClick={onCancel} type="button">
                Cancel
              </SecondaryButton>
              <PrimaryButton disabled={isSubmitting || formikSubmitting} type="submit">
                {isSubmitting || formikSubmitting ? "Saving..." : submitLabel || "Save user"}
              </PrimaryButton>
            </ActionRow>
          </FormStack>
        </form>
      );
    }}
  </Formik>
);
